import { useTranslation } from "react-i18next"
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"

import type { PiePlotView, RecordType } from "types/dashboard.types"
import type {
  QualityDescriptionParams,
  QualityLevel,
  QualityRecord,
} from "types/quality.types"
import PopUp from "widgets/common/PopUp"
import MuiPieChart from "./MuiPieChart"

const QUALITY_LEVELS: QualityLevel[] = ["healthy", "degraded", "unreliable", "invalid"]

interface QualityDialogProps {
  open: boolean
  onClose: () => void
  descriptionParams?: QualityDescriptionParams
  nonZeroQualities: QualityRecord[]
  pieView: PiePlotView
  pieRecords: RecordType[]
}

const QualityDialog = ({
  open,
  onClose,
  descriptionParams,
  nonZeroQualities,
  pieView,
  pieRecords,
}: QualityDialogProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <PopUp
      data-testid="quality-dialog"
      open={open}
      onClose={onClose}
      titleKey="quality.DATA_QUALITY"
      maxWidth="md"
      fullWidth
    >
      {descriptionParams && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("quality.TELEMETRY_QUALITY_DESC", descriptionParams)}
        </Typography>
      )}
      <Stack
        direction={isCompact ? "column" : "row"}
        gap={3}
        alignItems={isCompact ? "center" : "flex-start"}
        justifyContent="center"
      >
        <MuiPieChart view={pieView} data={pieRecords} showLegend={false} />

        <Stack gap={1} pt={2}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("quality.SUMMARY")}
          </Typography>
          {QUALITY_LEVELS.map((level) => {
            const record = nonZeroQualities.find((q) => q.name === level)
            const value =
              record && record.percentage > 0 ? `${record.percentage.toFixed(1)}%` : "–"
            return (
              <Stack key={level} direction="row" alignItems="flex-start" gap={1.5}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.quality[level].main,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(`quality.${level.toUpperCase()}`)}: {value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`quality.${level.toUpperCase()}_DESC`)}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {t("quality.TELEMETRY_QUALITY_API_NOTE")}
      </Typography>
    </PopUp>
  )
}

export default QualityDialog
